import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PointsApiProviderService, WorkspaceApiProviderService } from '@core/api';
import { catchError, EMPTY, finalize, Observable, of, shareReplay, tap } from 'rxjs';
import { logEventInGTAG } from '../../services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventRelationship,
} from '../../services/analytics/google-analytics.consts';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { ResponseErrorService } from '../../modules/errors/response-error.service';
import { PointActivityService } from '../../modules/points/point-modal/point-timeline/point-activity.service';
import { TPointLightweight } from '../../modules/points/points.model';
import { CustomTableService } from '../../modules/site/site-table/custom-table/custom-table.service';
import { SiteTableEventsService } from '../../modules/site/site-table/site-table-events.service';
import { EWorkspaces } from '../../modules/workspace/workspaces.enum';
import { PromptService } from '../prompt/prompt.service';
import { RelatedPointsCacheService } from './related-points-cache.service';
import { TRelatedPoint, TRelatedPointPayload, TSiteLightweight } from './related-points.consts';

@Injectable({
  providedIn: 'root',
})
export class RelatedPointsService {
  private pendingSitesRequests: { [accountId: string]: Observable<TSiteLightweight[]> } = {};
  private pendingPointsRequests: { [workspaceId: string]: Observable<TPointLightweight[]> } = {};

  constructor(
    private pointsApiProviderService: PointsApiProviderService,
    private workspaceApiProviderService: WorkspaceApiProviderService,
    private responseErrorService: ResponseErrorService,
    private promptService: PromptService,
    private translationPipe: TranslationPipe,
    private siteTableEventsService: SiteTableEventsService,
    private router: Router,
    private pointActivityService: PointActivityService,
    private relatedPointsCacheService: RelatedPointsCacheService,
    private customTableService: CustomTableService,
  ) {}

  fetchSites(accountId: string): Observable<TSiteLightweight[]> {
    const pendingRequest = this.pendingSitesRequests[accountId];

    if (pendingRequest) {
      return pendingRequest;
    }

    const request = this.workspaceApiProviderService
      .fetchSitesForPointRelationships(accountId)
      .pipe(
        catchError((error) => {
          const promptText = this.translationPipe.transform('related_points_sites_load_error');

          this.responseErrorService.handleRequestError(error, 'Failed to fetch sites');
          this.promptService.showError(promptText);

          return of([]);
        }),
        finalize(() => {
          this.pendingSitesRequests = Object.fromEntries(
            Object.entries(this.pendingSitesRequests).filter(([key]) => key !== accountId),
          );
        }),
        shareReplay(),
      );

    this.pendingSitesRequests[accountId] = request;

    return request;
  }

  fetchPoints(workspaceId: string): Observable<TPointLightweight[]> {
    const pendingRequest = this.pendingPointsRequests[workspaceId];

    if (pendingRequest) {
      return pendingRequest;
    }

    const request = this.pointsApiProviderService.fetchPointsLightweight(workspaceId).pipe(
      catchError((error) => {
        const promptText = this.translationPipe.transform('related_points_points_load_error');

        this.responseErrorService.handleRequestError(error, 'Failed to fetch points');
        this.promptService.showError(promptText);

        return of([]);
      }),
      finalize(() => {
        this.pendingPointsRequests = Object.fromEntries(
          Object.entries(this.pendingPointsRequests).filter(([key]) => key !== workspaceId),
        );
      }),
      shareReplay(),
    );

    this.pendingPointsRequests[workspaceId] = request;

    return request;
  }

  addRelation({
    sourcePointId,
    relation,
    workspaceId,
    isNewPoint,
  }: {
    sourcePointId: string;
    relation: TRelatedPoint;
    workspaceId: string;
    isNewPoint: boolean;
  }): Observable<TRelatedPoint> {
    if (isNewPoint) {
      this.relatedPointsCacheService.addPendingRelationship(sourcePointId, relation);

      return of(relation);
    }

    const payload: TRelatedPointPayload = {
      type: relation.type,
      relatedPointId: relation.pointId,
    };

    return this.pointsApiProviderService.addRelation(payload, sourcePointId).pipe(
      tap(() => {
        this.pointActivityService.refreshTimeline(workspaceId, sourcePointId);
        this.customTableService.reloadTable({ preserveTableState: true });
        this.showRelationAddedFeedback();
      }),
      catchError((error) => {
        const promptText: string = this.translationPipe.transform('prompt_relation_add_error');

        this.promptService.showError(promptText);
        this.responseErrorService.handleRequestError(error, 'Failed to add relation');

        return EMPTY;
      }),
    );
  }

  deleteRelation({
    pointId,
    relatedPointId,
    workspaceId,
    isNewPoint,
  }: {
    pointId: string;
    relatedPointId: string;
    workspaceId: string;
    isNewPoint: boolean;
  }): Observable<never> {
    if (isNewPoint) {
      this.relatedPointsCacheService.removePendingRelationship(pointId, relatedPointId);

      return of(null) as Observable<never>;
    }

    return this.pointsApiProviderService.deleteRelation(pointId, relatedPointId).pipe(
      tap(() => {
        this.pointActivityService.refreshTimeline(workspaceId, pointId);
        this.customTableService.reloadTable({ preserveTableState: true });
        this.showRelationDeletedFeedback();
      }),
      catchError((error) => {
        const promptText: string = this.translationPipe.transform('prompt_relation_delete_error');

        this.promptService.showError(promptText);
        this.responseErrorService.handleRequestError(error, 'Failed to delete relation');

        return EMPTY;
      }),
    );
  }

  private showRelationAddedFeedback(): void {
    const promptText: string = this.translationPipe.transform('prompt_relation_added');
    this.promptService.showSuccess(promptText);

    logEventInGTAG(EGoogleEventRelationship.ADD_RELATIONSHIP, {
      source: EGoogleEventCategory.POINT_RELATIONSHIPS,
    });
  }

  private showRelationDeletedFeedback(): void {
    const promptText: string = this.translationPipe.transform('prompt_relation_deleted');
    this.promptService.showSuccess(promptText);

    logEventInGTAG(EGoogleEventRelationship.DELETE_RELATIONSHIP, {
      source: EGoogleEventCategory.POINT_RELATIONSHIPS,
    });
  }

  openRelatedPoint({ pointId, workspaceId }: { pointId: string; workspaceId: string }): void {
    const url: string = this.router.url;
    const isOverview: boolean = url.includes(EWorkspaces.OVERVIEW);
    const isTimeline: boolean = url.includes(EWorkspaces.TIMELINE);

    this.siteTableEventsService.openPoint(
      { _id: pointId, workspaceId },
      { isOverview, isTimeline },
    );
  }
}
